import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "../../hooks/useAuth"

const SuspendAccountModal = ({ isOpen, onClose, onConfirm, hasPassword = true }) => {
  const { t, i18n } = useTranslation()
  const { getUserType } = useAuth()

  // Get user type color
  const getUserTypeColor = () => {
    const userType = getUserType()
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
    }
    return userTypeConfig[userType] || '#8548A8' // Default to purple
  }

  // Helper function to darken color for hover
  const darkenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16)
    const amt = Math.round(2.55 * percent)
    const R = Math.max(0, Math.min(255, (num >> 16) + amt))
    const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt))
    const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt))
    return "#" + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)
  }

  const userTypeColor = getUserTypeColor()
  const hoverColor = darkenColor(userTypeColor, -15)

  const [password, setPassword] = useState("")
  const [step, setStep] = useState(1) // 1: warning, 2: password

  const handleNext = () => {
    setStep(2)
  }

  const handleBack = () => {
    setStep(1)
  }

  const handleConfirm = () => {
    // For OAuth users without password, send null or undefined
    onConfirm(hasPassword ? password : null)
  }

  const handleNextOrConfirm = () => {
    if (hasPassword) {
      setStep(2) // Go to password step
    } else {
      onConfirm(null) // Confirm directly without password
    }
  }

  const handleClose = () => {
    setPassword("")
    setStep(1)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: "16px",
          padding: "32px 40px 40px 40px",
          maxWidth: "480px",
          width: "100%",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X Button */}
        <button
          onClick={handleClose}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            borderRadius: "8px",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#F9FAFB"
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "transparent"
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6B7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Modal Content */}
        <div style={{ marginTop: "20px" }} dir={i18n.dir()}>
          <h2
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "24px",
              fontWeight: 600,
              color: "#303E4E",
              margin: "0 0 16px 0",
              textAlign: "center",
            }}
          >
            {step === 1
              ? t("accountManagement.modals.suspend.title")
              : t("accountManagement.modals.suspend.confirmTitle") || "Confirm Suspension"}
          </h2>

          {step === 1 ? (
            // Step 1: Warning Message
            <div
              style={{
                animation: "fadeIn 0.3s ease-in-out",
              }}
            >
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "16px",
                  color: "#6B7280",
                  lineHeight: "1.6",
                  margin: "0 0 32px 0",
                  textAlign: "center",
                }}
              >
                {t("accountManagement.modals.suspend.description")}
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={handleClose}
                  style={{
                    padding: "12px 24px",
                    background: "#9CA3AF",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: "12px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    minWidth: "100px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#6B7280"
                    e.target.style.transform = "translateY(-1px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#9CA3AF"
                    e.target.style.transform = "translateY(0)"
                  }}
                >
                  {t("accountManagement.modals.suspend.cancel") || "Cancel"}
                </button>

                <button
                  onClick={handleNextOrConfirm}
                  style={{
                    padding: "12px 24px",
                    background: userTypeColor,
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: "12px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    minWidth: "140px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = hoverColor
                    e.target.style.transform = "translateY(-1px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = userTypeColor
                    e.target.style.transform = "translateY(0)"
                  }}
                >
                  {hasPassword
                    ? (t("accountManagement.modals.suspend.next") || "Next")
                    : (t("accountManagement.modals.suspend.confirm") || "Confirm Suspension")
                  }
                </button>
              </div>
            </div>
          ) : (
            // Step 2: Password Input
            <div
              style={{
                animation: "fadeIn 0.3s ease-in-out",
              }}
            >
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  color: "#6B7280",
                  lineHeight: "1.6",
                  margin: "0 0 24px 0",
                  textAlign: "center",
                }}
              >
                {t("accountManagement.modals.suspend.passwordPrompt") || "Please enter your password to confirm account suspension."}
              </p>

              <div style={{ marginBottom: "24px", textAlign: "left" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    color: "#374151",
                    fontFamily: '"Red Hat Display", sans-serif',
                  }}
                >
                  {t("common.password") || "Password"}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("common.enterPassword") || "Enter your password"}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #D1D5DB",
                    fontSize: "16px",
                    outline: "none",
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={handleBack}
                  style={{
                    padding: "12px 24px",
                    background: "#9CA3AF",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: "12px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    minWidth: "100px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#6B7280"
                    e.target.style.transform = "translateY(-1px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#9CA3AF"
                    e.target.style.transform = "translateY(0)"
                  }}
                >
                  {t("accountManagement.modals.suspend.back") || "Back"}
                </button>

                <button
                  onClick={handleConfirm}
                  disabled={!password}
                  style={{
                    padding: "12px 24px",
                    background: password ? "#DC2626" : "#D1D5DB",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: "12px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 600,
                    cursor: password ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease",
                    minWidth: "140px",
                  }}
                  onMouseEnter={(e) => {
                    if (password) {
                      e.target.style.background = "#B91C1C"
                      e.target.style.transform = "translateY(-1px)"
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (password) {
                      e.target.style.background = "#DC2626"
                      e.target.style.transform = "translateY(0)"
                    }
                  }}
                >
                  {t("accountManagement.modals.suspend.confirm")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SuspendAccountModal

// Add CSS animation
const style = document.createElement('style')
style.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`
if (typeof document !== 'undefined' && !document.getElementById('suspend-modal-animations')) {
  style.id = 'suspend-modal-animations'
  document.head.appendChild(style)
}